/**
 * Local work images from src/assets/new — keyed by `${collection}:${id}`.
 * Filenames must match files in that folder.
 */
const files = require.context("./new", false, /\.(jpe?g|png|webp|avif)$/i);

function asset(name) {
  try {
    return files(`./${name}`);
  } catch {
    return null;
  }
}

/** Explicit matches from Firebase filenames / titles → local files */
const BY_KEY = {
  // documentary
  "documentary:0.001": "image3.jpeg",
  "documentary:0.002": "image2.png",
  "documentary:0.01": "tua madre 2.jpg",
  "documentary:1": "paradox effect.jpg",
  "documentary:2": "cervati.jpg",
  "documentary:4": "maestri di sci.jpg",
  "documentary:5": "free spirits.jpg",
  "documentary:6": "stabat mater.jpg",
  "documentary:7": "Collective.jpg",
  "documentary:9": "Elvis.jpg",
  "documentary:9.9": "il dono oscuro.jpg",
  "documentary:9.91":
    "SnapInsta.to_527446992_17898017952271067_7045894167374351334_n.jpg",
  "documentary:9.92": "EXA.png",

  // commercial
  "commercial:0.01": "SUPERGIRL.jpeg",
  "commercial:UL": "goa ultrabeat.jpg",
  "commercial:V": "goa modificato.jpg",
  "commercial:v1": "gay help line.jpg",
  "commercial:v2":
    "SnapInsta.to_527446992_17898017952271067_7045894167374351334_n.jpg",

  // press
  "press:4": "Elvis.jpg",
  "press:6": "LUNATIK.jpeg",

  // lunatik
  "lunatik:YqfPZunTQrExuVQSqTrS": "LUNATIK.jpeg",
  "lunatik:7": "Picsart_25-04-09_17-28-55-918.jpg",
  "lunatik:8": "Picsart_25-04-09_17-27-39-920.jpg",
  "lunatik:ggfsXkaxgZCmZ7iU7iC2": "Picsart_25-04-09_17-26-59-785.jpg",
  "lunatik:6MnyMyDyaguVJOlLw2e0": "Picsart_25-04-09_17-25-34-626.jpg",
};

/** Title fallbacks (normalized) for items without a stable id match */
const BY_TITLE = {
  "san diego ita": "image3.jpeg",
  "tua madre ita": "tua madre 2.jpg",
  "paradox effect usa": "paradox effect.jpg",
  "cervati il respiro di un monte rai": "cervati.jpg",
  "maestri di sci": "maestri di sci.jpg",
  "free spirits": "free spirits.jpg",
  "stabat mater": "stabat mater.jpg",
  "elvis diventare grande": "Elvis.jpg",
  "il dono oscuro": "il dono oscuro.jpg",
  "prima morte poi paradiso":
    "SnapInsta.to_527446992_17898017952271067_7045894167374351334_n.jpg",
  "sound logo": "EXA.png",
  supergirl: "SUPERGIRL.jpeg",
  "we always put music first": "goa ultrabeat.jpg",
  "ultra bites vol 01": "goa modificato.jpg",
  "helpline adv": "gay help line.jpg",
  "resident advisor": "LUNATIK.jpeg",
  "japan tour 2025": "Picsart_25-04-09_17-28-55-918.jpg",
  "loud club terracina": "Picsart_25-04-09_17-27-39-920.jpg",
  "japan tour 2023": "Picsart_25-04-09_17-26-59-785.jpg",
};

function normalizeTitle(title) {
  return String(title || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function resolveWorkImage(item, collection) {
  if (!item) return null;

  const key = collection ? `${collection}:${item.id}` : null;
  if (key && BY_KEY[key]) {
    const url = asset(BY_KEY[key]);
    if (url) return url;
  }

  const titleKey = normalizeTitle(item.title || item["title "]);
  if (titleKey && BY_TITLE[titleKey]) {
    const url = asset(BY_TITLE[titleKey]);
    if (url) return url;
  }

  // Fuzzy: local file whose name is contained in title or vice versa
  const keys = files.keys();
  for (const path of keys) {
    const base = path
      .replace(/^\.\//, "")
      .replace(/\.[^.]+$/, "")
      .toLowerCase();
    const n = normalizeTitle(base);
    if (!n || n.length < 4) continue;
    if (titleKey.includes(n) || n.includes(titleKey)) {
      return files(path);
    }
  }

  return null;
}

const MEDIA_BASE = `${process.env.REACT_APP_SUPABASE_URL}/storage/v1/object/public/media/images`;

const HOSTED_EXT = {
  "documentary:0.002": "jpeg",
  "commercial:UI4SFFbSU8MVQ7dpL3Sb": "png",
};

function isFirebaseUrl(src) {
  return /firebasestorage\.googleapis\.com|appspot\.com/i.test(src || "");
}

function hostedImageUrl(collection, id) {
  if (!collection || id == null) return "";
  const key = `${collection}:${id}`;
  const ext = HOSTED_EXT[key] || "png";
  const safeId = String(id).replace(/[^a-zA-Z0-9._-]/g, "_");
  return `${MEDIA_BASE}/${collection}/${safeId}.${ext}`;
}

export function withLocalWorkImage(item, collection) {
  if (!item) return item;

  const remote = item.ImageUrl || item.image_url || "";
  const isHttp = typeof remote === "string" && /^https?:\/\//i.test(remote);

  if (collection === "landing_media") {
    return { ...item, ImageUrl: remote, image_url: remote };
  }

  // Prefer hosted URLs (Supabase / CDN). Rewrite broken Firebase billing URLs.
  if (isHttp && !isFirebaseUrl(remote)) {
    return item;
  }

  const local = resolveWorkImage(item, collection);
  if (local) {
    return { ...item, ImageUrl: local, image_url: local };
  }

  if (isFirebaseUrl(remote) || !remote) {
    const hosted = hostedImageUrl(collection, item.id);
    if (hosted) {
      return { ...item, ImageUrl: hosted, image_url: hosted };
    }
  }

  return item;
}
